import * as THREE from 'three';
import { SeededRandom } from '../utils/random.js';
import { planetPalette } from '../utils/palette.js';
import { buildCreatureMesh, animateCreature } from '../creatures/CreatureMeshBuilder.js';

/**
 * Hand-crafted story sites for the player's home planet.
 *
 * Unlike the procedural scatter, these are AUTHORED: fixed characters with
 * dialogue that reacts to quest progress, placed at landmark-scale sites the
 * player can spot from a distance and walk toward.
 *
 *  - The Crash Site — the previous traveler's wrecked ship, kept as a camp
 *    by KIP-3, their marooned survey robot.
 *  - The Song Circle — a ring of ancient monoliths where The Listener, a
 *    huge old creature, collects the planet's songs.
 */

/* ══════════════════════════════════════════════════════════════
 *  Dialogue — lines react to quest progress
 * ══════════════════════════════════════════════════════════════ */

function kipDialogue(gs) {
  if (gs.radioRepaired) {
    return [
      'You fixed it. I can hear it from here — the whole planet, all at once.',
      'That song… that\'s the captain\'s. They taught it to the creatures one phrase at a time, around this very fire.',
      'They always said a song outlives its singer. I used to think that was just something organics say.',
      'Go and listen. And when you make your own — sing it loud enough for an old robot to hear.',
    ];
  }
  if (gs.allToolsCollected) {
    return [
      'That\'s all four parts — coil, crystal, board, cell. The captain would be delighted. And insufferable about it.',
      'Take them to the ship radio. When it sings, you\'ll understand why we never really left.',
    ];
  }
  if (gs.collectedTools.length > 0) {
    const left = 4 - gs.collectedTools.length;
    return [
      `You found ${gs.collectedTools.length === 1 ? 'one of the captain\'s caches' : 'some of the captain\'s caches'}. ${left} still out there.`,
      'They scattered the radio parts on purpose, you know. Said anyone who wanted the song should walk the planet first.',
      'Check the ruins and the old monolith. The dark spires on the horizon — that\'s where I\'d look.',
    ];
  }
  return [
    'Oh! You\'re not a rock. Apologies — after four hundred days, one starts greeting the rocks.',
    'KIP-3, planetary survey unit, formerly of the cartography vessel you see… somewhat redistributed behind me.',
    'My captain didn\'t make it off this world. But they left something better than a grave — they left a transmission.',
    'The parts to receive it are cached in the ruins out there. Find all four, repair your radio, and you\'ll hear what they made.',
  ];
}

function listenerDialogue(gs) {
  if (gs.radioRepaired) {
    return [
      '…so. You made the stones worth standing.',
      'Every traveler leaves a song. The small ones carry it when the singer goes. That is all a world is — a choir with a long memory.',
      'When you go to another sky, leave yours loudly. I will add it to the circle.',
    ];
  }
  if (gs.allSpeciesHeard) {
    return [
      'You have heard all of them now. Each small voice, one thread.',
      'They are not singing at you. They are singing at each other, waiting for the one who can weave.',
      'Mend your machine. Then stand here, in the circle, and listen properly.',
    ];
  }
  return [
    '…hm. Footsteps that wonder. Rare, here.',
    'I have sat between these stones since before the stones. I collect what travelers leave behind. Not their metal — their intervals.',
    'The small ones out there each hold one note of something larger. Go and listen to every species. Then you may understand what I keep.',
  ];
}

const CHARACTERS = {
  kip: { name: 'KIP-3', dialogue: kipDialogue },
  listener: { name: 'The Listener', dialogue: listenerDialogue },
};

/* ══════════════════════════════════════════════════════════════
 *  Site construction
 * ══════════════════════════════════════════════════════════════ */

export class StorySites {
  constructor() {
    this.group = new THREE.Group();
    this.group.name = 'story-sites';
    this._npcs = [];        // { id, group, baseQuat, isCreature }
    this._flames = [];      // { mesh, light, phase }
    this._smoke = [];       // { mesh, phase, speed, maxHeight }
    this._built = false;
  }

  /** Character meta for the dialogue UI. */
  getCharacter(id) {
    return CHARACTERS[id] || null;
  }

  /**
   * Build both sites on the planet surface.
   * @param {object} planetData     planet config (visual.hue used for tinting)
   * @param {number} planetRadius
   * @param {function} getHeightFn  (normalizedDir) => surface height
   */
  build(planetData, planetRadius, getHeightFn) {
    this.clear();
    this._built = true;
    const pal = planetPalette(planetData.visual?.hue ?? 0.6);
    const rng = new SeededRandom((planetData.seed ?? 1) + 4242);

    // Anchors relative to the spawn direction (+Y): crash site a short walk
    // away, song circle on the far slope so its monoliths sit on the horizon.
    const crashDir = new THREE.Vector3(0.28, 1, 0.1).normalize();
    const circleDir = new THREE.Vector3(-0.55, 1, -0.75).normalize();

    this._buildCrashSite(crashDir, getHeightFn, pal, rng);
    this._buildSongCircle(circleDir, getHeightFn, pal, rng);
    return this.group;
  }

  /** Orient `obj` so its +Y matches the surface normal at dir, at height. */
  _place(obj, dir, getHeightFn, extraLift = 0) {
    const h = getHeightFn(dir) + extraLift;
    obj.position.copy(dir).multiplyScalar(h);
    obj.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
  }

  _buildCrashSite(dir, getHeightFn, pal, rng) {
    const site = new THREE.Group();

    const hullMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(pal.terrain, 0.12, 0.24),
      roughness: 0.55, metalness: 0.65,
    });
    const scorchMat = new THREE.MeshStandardMaterial({
      color: 0x1a1614, roughness: 0.95,
    });

    // Broken hull — half a torus rising from the ground like a ribcage
    const hull = new THREE.Mesh(new THREE.TorusGeometry(6, 1.4, 8, 20, Math.PI * 0.85), hullMat);
    hull.rotation.set(Math.PI / 2 + 0.35, 0.4, 0);
    hull.position.set(-2, 0.5, -6);
    hull.castShadow = true;
    site.add(hull);

    // Scattered hull plates
    for (let i = 0; i < 7; i++) {
      const plate = new THREE.Mesh(
        new THREE.BoxGeometry(rng.range(1.2, 3), 0.22, rng.range(1, 2.2)),
        rng.next() < 0.3 ? scorchMat : hullMat,
      );
      plate.position.set(rng.range(-9, 9), 0.15, rng.range(-9, 4));
      plate.rotation.set(rng.range(-0.3, 0.3), rng.range(0, Math.PI), rng.range(-0.25, 0.25));
      plate.castShadow = true;
      site.add(plate);
    }

    // Campfire: stone ring + ember glow + flame + light
    const fire = new THREE.Group();
    fire.position.set(2.2, 0, 1.5);
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2;
      const stone = new THREE.Mesh(new THREE.DodecahedronGeometry(0.28, 0), scorchMat);
      stone.position.set(Math.cos(a) * 0.9, 0.18, Math.sin(a) * 0.9);
      fire.add(stone);
    }
    const flame = new THREE.Mesh(
      new THREE.ConeGeometry(0.42, 1.1, 7),
      new THREE.MeshStandardMaterial({
        color: 0xffa040, emissive: 0xff7722, emissiveIntensity: 2.2,
        transparent: true, opacity: 0.9,
      }),
    );
    flame.position.y = 0.65;
    fire.add(flame);
    const fireLight = new THREE.PointLight(0xff8844, 14, 26, 1.8);
    fireLight.position.y = 1.2;
    fire.add(fireLight);
    site.add(fire);
    this._flames.push({ mesh: flame, light: fireLight, phase: rng.next() * 10 });

    // Smoke column — the "follow the smoke" landmark. Tall enough to clear
    // the treeline so the camp is findable from the landing site.
    const maxHeight = 30;
    for (let i = 0; i < 6; i++) {
      const puff = new THREE.Mesh(
        new THREE.SphereGeometry(1, 8, 8),
        new THREE.MeshStandardMaterial({
          color: 0x9aa0b0, transparent: true, opacity: 0.3, depthWrite: false,
        }),
      );
      const phase = i / 6;
      fire.add(puff);
      this._smoke.push({ mesh: puff, phase, speed: 0.05 + rng.next() * 0.02, maxHeight });
    }

    // KIP-3 — a squat, friendly survey robot by the fire
    const kip = this._buildKip(pal);
    kip.name = 'story-kip';
    kip.position.set(4.6, 0, 3.4);
    kip.lookAt(fire.position.x, 1, fire.position.z);
    site.add(kip);
    this._npcs.push({ id: 'kip', group: kip, baseQuat: kip.quaternion.clone(), isCreature: false });

    this._place(site, dir, getHeightFn);
    this.group.add(site);
  }

  _buildKip(pal) {
    const g = new THREE.Group();
    const bodyMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(pal.accent, 0.25, 0.6),
      roughness: 0.4, metalness: 0.55,
    });
    const darkMat = new THREE.MeshStandardMaterial({ color: 0x22242e, roughness: 0.6, metalness: 0.3 });
    const eyeMat = new THREE.MeshStandardMaterial({
      color: 0xbfe3ff, emissive: 0x66aaff, emissiveIntensity: 1.6,
    });

    // Treads
    const tread = new THREE.Mesh(new THREE.BoxGeometry(1.5, 0.5, 1.9), darkMat);
    tread.position.y = 0.25;
    g.add(tread);
    // Body
    const body = new THREE.Mesh(new THREE.BoxGeometry(1.15, 1.05, 1.3), bodyMat);
    body.position.y = 1.02;
    body.castShadow = true;
    g.add(body);
    // Head (swivels toward the player at runtime)
    const head = new THREE.Group();
    head.position.y = 1.9;
    const skull = new THREE.Mesh(new THREE.BoxGeometry(0.85, 0.62, 0.8), bodyMat);
    skull.castShadow = true;
    head.add(skull);
    const eye = new THREE.Mesh(new THREE.SphereGeometry(0.16, 10, 10), eyeMat);
    eye.position.set(0, 0.02, 0.42);
    head.add(eye);
    const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 0.7, 6), darkMat);
    antenna.position.set(0.28, 0.6, 0);
    head.add(antenna);
    const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.07, 8, 8), eyeMat);
    bulb.position.set(0.28, 0.98, 0);
    head.add(bulb);
    g.add(head);
    g.userData.head = head;
    return g;
  }

  _buildSongCircle(dir, getHeightFn, pal, rng) {
    const site = new THREE.Group();

    const stoneMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(pal.terrain, 0.15, 0.3),
      roughness: 0.85,
    });
    const glyphMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color().setHSL(pal.accent, 0.7, 0.6),
      emissive: new THREE.Color().setHSL(pal.accent, 0.8, 0.45),
      emissiveIntensity: 1.4,
    });

    // Ring of tall monoliths — landmark-scale, visible from far away
    const n = 7;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2;
      const h = 9 + rng.next() * 5;
      const stone = new THREE.Mesh(new THREE.BoxGeometry(2.2, h, 1.1), stoneMat);
      stone.position.set(Math.cos(a) * 11, h / 2 - 0.4, Math.sin(a) * 11);
      stone.rotation.y = -a + rng.range(-0.15, 0.15);
      stone.rotation.z = rng.range(-0.05, 0.05);
      stone.castShadow = true;
      site.add(stone);

      // Glowing glyph strip on each stone's inner face
      const glyph = new THREE.Mesh(new THREE.BoxGeometry(0.5, h * 0.55, 0.1), glyphMat);
      glyph.position.set(Math.cos(a) * 10.35, h * 0.45, Math.sin(a) * 10.35);
      glyph.rotation.y = -a;
      site.add(glyph);
    }

    // Center dais
    const dais = new THREE.Mesh(new THREE.CylinderGeometry(4.2, 4.8, 0.7, 9), stoneMat);
    dais.position.y = 0.35;
    dais.receiveShadow = true;
    site.add(dais);

    // The Listener — an ancient, oversized creature seated on the dais
    const listener = buildCreatureMesh({
      id: 'story_listener',
      index: 999,
      speciesId: 777,
      bodyType: 'biped',
      size: 3.4,
      color: new THREE.Color().setHSL(pal.accent, 0.35, 0.55).getHex(),
      eyeStyle: 'triple',
      limbCount: 2,
    });
    listener.name = 'story-listener';
    listener.position.y = 0.7;
    site.add(listener);
    this._npcs.push({ id: 'listener', group: listener, baseQuat: null, isCreature: true });

    this._place(site, dir, getHeightFn);
    this.group.add(site);
  }

  /**
   * Per-frame: flame flicker, Listener idle animation, Kip's head tracking
   * the player when they're near.
   * @param {THREE.Vector3|null} playerWorldPos
   */
  update(dt, time, playerWorldPos = null) {
    if (!this._built) return;

    for (const f of this._flames) {
      const flick = 0.85 + Math.sin(time * 11 + f.phase) * 0.1 + Math.sin(time * 23 + f.phase) * 0.05;
      f.mesh.scale.set(flick, 0.9 + flick * 0.25, flick);
      f.light.intensity = 12 + flick * 5;
    }

    // Smoke puffs rise, swell, and thin out, then recycle at the fire.
    for (const s of this._smoke) {
      s.phase += dt * s.speed;
      if (s.phase >= 1) s.phase -= 1;
      const t = s.phase;
      s.mesh.position.set(
        Math.sin(time * 0.4 + t * 9) * (0.4 + t * 2.5),
        1.5 + t * s.maxHeight,
        Math.cos(time * 0.3 + t * 7) * (0.4 + t * 2.5),
      );
      s.mesh.scale.setScalar(0.7 + t * 3.2);
      s.mesh.material.opacity = 0.34 * (1 - t * t) + 0.02;
    }

    for (const npc of this._npcs) {
      if (npc.isCreature) {
        animateCreature(npc.group, time, 0); // idle breathing/sway
      } else if (playerWorldPos && npc.group.userData.head) {
        // Head swivel: look toward the player if within ~14 units
        const head = npc.group.userData.head;
        const headWorld = new THREE.Vector3();
        head.getWorldPosition(headWorld);
        if (headWorld.distanceTo(playerWorldPos) < 14) {
          const local = npc.group.worldToLocal(playerWorldPos.clone());
          const target = Math.atan2(local.x, local.z);
          head.rotation.y += (target - head.rotation.y) * Math.min(1, dt * 5);
        } else {
          head.rotation.y += (0 - head.rotation.y) * Math.min(1, dt * 2);
        }
      }
    }
  }

  clear() {
    this.group.traverse(child => {
      if (child.geometry) child.geometry.dispose();
      if (child.material) {
        if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
        else child.material.dispose();
      }
    });
    while (this.group.children.length) this.group.remove(this.group.children[0]);
    this._npcs = [];
    this._flames = [];
    this._smoke = [];
    this._built = false;
  }
}
